import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/829bfe77-138f-4e8f-a560-e955ac13fa9d/images/69d5cbfc7af5daf99e9fad8ba3637783a7c4086b74dc4369571c1ee39b404371.png";

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    document.title = "Sign in · RigBooks";
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Enter your email and password.");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  const signInWithGoogle = () => {
    setGoogleBusy(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      `https://auth.emergentagent.com/?app_name=RigBooks&name=RigBooks` +
      `&access_type=offline&prompt=consent` +
      `&redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen bg-rig-bg lg:grid-cols-2">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-rig-bg via-rig-bg/70 to-rig-bg/30" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Logo size={40} />
          <div>
            <h2 className="font-display text-5xl font-bold uppercase leading-[1.05] tracking-wide text-white">
              Audit-ready expenses.
              <br />
              <span className="text-rig-accent">Built for the long haul.</span>
            </h2>
            <p className="mt-4 max-w-md text-rig-dim">
              Snap receipts, track hauls, file IFTA and hand your CPA a clean report — all from the cab.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Logo size={38} />
          </div>
          <div className="rounded-3xl border border-rig-border bg-rig-surface p-7 sm:p-9">
            <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-dim">
              Sign in to your rig
            </p>

            <Button
              type="button"
              onClick={signInWithGoogle}
              disabled={googleBusy || busy}
              data-testid="google-sign-in-button"
              className="mt-5 h-12 w-full gap-3 bg-white text-[15px] font-semibold text-slate-900 hover:bg-slate-100"
            >
              {googleBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleMark />}
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-rig-border" />
              <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-rig-mute">
                Or email
              </span>
              <div className="h-px flex-1 bg-rig-border" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="driver@example.com"
                  data-testid="login-email-input"
                  className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                  autoComplete="current-password"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="h-12 w-full bg-rig-accent font-display text-base font-bold uppercase tracking-[0.2em] text-rig-accentText hover:bg-rig-accentDark"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
              </Button>
            </form>

            <div className="mt-5 flex items-center justify-between text-sm">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="font-semibold text-rig-accent hover:underline"
              >
                Forgot password?
              </Link>
              <Link to="/register" data-testid="go-to-register-link" className="text-rig-dim hover:text-white">
                New driver? <span className="font-semibold text-rig-accent">Create account</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
